/**
 * Partnerships Hub → la FICHA de un pack como DATA (pedido de Partnerships 13/10/2026).
 *
 * Un solo registro describe los campos simples de la ficha (sección, tipo, interno)
 * y lo consumen tres cosas: el formulario (renderiza cada sección), el detalle
 * (muestra cada sección) y el contexto del asistente del hub (lee lo que NO es
 * interno). Un campo simple nuevo = una entrada acá + sus claves i18n
 * `partnerships.package_sheet.*` (es/en/pt); form, detalle, completitud y asistente
 * lo toman solos. Lo que no es un campo simple (variantes, elegibilidad, links) va
 * como `custom` y cada consumidor lo renderiza a mano.
 *
 * Misma idea que OML_SCHEMA (shared/types/oml.ts), sin acoplarse a OML: acá no hay
 * fieldVersions, borradores ni pedidos de acceso. Lo INTERNO no se pide: se marca,
 * se muestra a todo Winclap y nunca sale al cliente ni al asistente
 * (`externalPackageView`). Tests en backend/src/lib/partnerships-mapping.test.ts.
 */
import { PACKAGE_SALES_MODELS } from './types/partnerships';
import type { Package, PackageVariant } from './types/partnerships';
import { effectiveVariants } from './partnerships-packages';

export type PackageSheetFieldType = 'text' | 'textarea' | 'url' | 'date' | 'select';

export interface PackageSheetField {
  /** Clave i18n (`partnerships.package_sheet.fields.<key>`) y nombre en el form. */
  key: string;
  /** Ruta en el pack ("conditions.minRoi"). */
  path: string;
  type: PackageSheetFieldType;
  options?: readonly string[];
  /** Etiqueta por defecto (es): la usa el asistente y es el `defaultValue` de i18n. */
  label: string;
  help?: string;
  /** Ocupa todo el ancho del form. */
  wide?: boolean;
}

export type PackageSheetCustom = 'variants' | 'eligibility' | 'links';

export interface PackageSheetSection {
  key: string;
  label: string;
  /** INTERNO: badge en la UI, fuera de la vista externa y del asistente. */
  internal?: boolean;
  fields: readonly PackageSheetField[];
  /** Bloque que no es un campo simple y cada consumidor renderiza a mano. */
  custom?: PackageSheetCustom;
}

export const PACKAGE_SHEET: readonly PackageSheetSection[] = [
  {
    key: 'context', label: 'Contexto general',
    fields: [
      { key: 'context', path: 'context', type: 'textarea', label: 'Objetivo estratégico del pack y contexto del partner', wide: true },
    ],
  },
  {
    key: 'variants', label: 'Entregables y precio por variante', custom: 'variants', fields: [],
  },
  {
    key: 'workflow', label: 'SLAs / workflow',
    fields: [
      { key: 'workflow', path: 'workflow', type: 'textarea', label: 'Cómo se opera: pasos, tiempos y SLAs', wide: true },
    ],
  },
  {
    key: 'sales', label: 'Modelo de venta',
    fields: [
      { key: 'salesModel', path: 'salesModel', type: 'select', options: PACKAGE_SALES_MODELS, label: 'Quién vende', help: 'El precio siempre lo fija el partner, venda quien venda.' },
    ],
  },
  {
    key: 'eligibility', label: 'Elegibilidad', custom: 'eligibility',
    fields: [
      { key: 'eligibilityNotes', path: 'eligibility.notes', type: 'textarea', label: 'Notas de elegibilidad', wide: true },
    ],
  },
  {
    key: 'conditions', label: 'Condiciones comerciales',
    fields: [
      { key: 'minRoi', path: 'conditions.minRoi', type: 'text', label: 'ROI mínimo' },
      { key: 'minMediaSpend', path: 'conditions.minMediaSpend', type: 'text', label: 'Media spend mínimo' },
      { key: 'investmentWindow', path: 'conditions.investmentWindow', type: 'text', label: 'Ventana de inversión' },
      { key: 'flexible', path: 'conditions.flexible', type: 'textarea', label: 'Qué es flexible', wide: true },
    ],
  },
  {
    key: 'economics', label: 'Economics internos', internal: true,
    fields: [
      { key: 'incentive', path: 'economics.incentive', type: 'text', label: 'Incentivo' },
      { key: 'costPerAsset', path: 'economics.costPerAsset', type: 'text', label: 'Costo por asset' },
      { key: 'margin', path: 'economics.margin', type: 'text', label: 'Margen' },
      { key: 'economicsNotes', path: 'economics.notes', type: 'textarea', label: 'Notas de economics', wide: true },
    ],
  },
  {
    key: 'external', label: 'Vista externa',
    fields: [
      { key: 'shareable', path: 'externalView.shareable', type: 'textarea', label: 'Qué se puede compartir con el cliente', wide: true },
      { key: 'materialUrl', path: 'externalView.materialUrl', type: 'url', label: 'Link al material externo', wide: true },
    ],
  },
  {
    key: 'partnerRules', label: 'Reglas con el partner',
    fields: [
      { key: 'partnerRules', path: 'partnerRules', type: 'textarea', label: 'Heads-ups, tiempos de espera, cuándo va vía agencia de medios', wide: true },
    ],
  },
  {
    key: 'restrictions', label: 'Restricciones de producto',
    fields: [
      { key: 'restrictions', path: 'restrictions', type: 'textarea', label: 'Restricciones', wide: true },
    ],
  },
  {
    key: 'validity', label: 'Vigencia',
    fields: [
      { key: 'effectiveDate', path: 'effectiveDate', type: 'date', label: 'Vigente desde' },
      { key: 'validUntil', path: 'validUntil', type: 'date', label: 'Vigente hasta' },
    ],
  },
  {
    key: 'links', label: 'Links útiles', custom: 'links',
    fields: [
      { key: 'driveUrl', path: 'driveUrl', type: 'url', label: 'One-pager (Drive)', wide: true },
    ],
  },
];

export const PACKAGE_SHEET_FIELDS: readonly PackageSheetField[] = PACKAGE_SHEET.flatMap((s) => s.fields);

/** Etiquetas por defecto (es) de los enums de la ficha; i18n `partnerships.package_sheet.enums.<valor>`. */
export const PACKAGE_SHEET_ENUM_LABELS: Record<string, string> = {
  winclap_direct: 'Winclap directo',
  partner: 'Lo vende el partner',
  both: 'Ambos',
  new: 'Cliente nuevo',
  repeated: 'Cliente recurrente',
};
// `both` es a la vez modelo de venta y tipo de cliente: misma etiqueta, dos listas.

// ── Lectura / escritura por ruta ────────────────────────────────────────────

export function getPath(obj: unknown, path: string): unknown {
  let cur: any = obj;
  for (const k of path.split('.')) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = cur[k];
  }
  return cur;
}

/**
 * Copia de `obj` con `path` = `value`. Vacío (`''`, `undefined`, `null`) borra la
 * clave, y un objeto intermedio que queda sin claves también se borra: así
 * `conditions` no queda como `{}` en DynamoDB.
 */
export function setPath<T extends object>(obj: T, path: string, value: unknown): T {
  const [head, ...rest] = path.split('.');
  const out: any = { ...obj };
  const empty = value === '' || value == null;
  if (rest.length === 0) {
    if (empty) delete out[head]; else out[head] = value;
    return out;
  }
  const child = setPath((out[head] && typeof out[head] === 'object' ? out[head] : {}) as object, rest.join('.'), value);
  if (Object.keys(child).length === 0) delete out[head]; else out[head] = child;
  return out;
}

const filled = (v: unknown): boolean =>
  v != null && (typeof v !== 'string' || v.trim() !== '') && (!Array.isArray(v) || v.length > 0);

/** ¿La sección tiene algo cargado? (sus campos simples o su bloque custom). */
export function sectionFilled(pkg: Package, section: PackageSheetSection): boolean {
  if (section.fields.some((f) => filled(getPath(pkg, f.path)))) return true;
  switch (section.custom) {
    case 'variants': return effectiveVariants(pkg).length > 0;
    case 'eligibility': return filled(pkg.eligibility?.segments) || filled(pkg.eligibility?.accountSizes) || filled(pkg.eligibility?.clientType);
    case 'links': return filled(pkg.links);
    default: return false;
  }
}

export interface SheetCompleteness {
  filled: number;
  total: number;
  /** Claves de las secciones vacías, en el orden de la ficha. */
  missing: string[];
}

/** Cuántos bloques de la ficha están cargados (misma idea que la completitud de OML). */
export function sheetCompleteness(pkg: Package): SheetCompleteness {
  const missing = PACKAGE_SHEET.filter((s) => !sectionFilled(pkg, s)).map((s) => s.key);
  return { filled: PACKAGE_SHEET.length - missing.length, total: PACKAGE_SHEET.length, missing };
}

/**
 * El pack sin nada interno: fuera las secciones `internal` y las notas (del pack y
 * de cada variante). Es lo ÚNICO que ve el asistente del hub y lo que se usaría
 * para cualquier salida hacia el cliente o el partner.
 */
export function externalPackageView(pkg: Package): Package {
  let out: Package = { ...pkg, notes: undefined };
  for (const s of PACKAGE_SHEET) {
    if (!s.internal) continue;
    for (const f of s.fields) {
      // El objeto entero: si mañana `economics` suma una clave que nadie registró, igual no sale.
      const head = f.path.split('.')[0];
      out = f.path.includes('.') ? setPath(out, head, undefined) : setPath(out, f.path, undefined);
    }
  }
  if (out.variants) out.variants = out.variants.map((v) => ({ ...v, notes: undefined }));
  return out;
}

/** Secciones internas → claves de primer nivel que la vista externa borra (para el test). */
export const INTERNAL_TOP_LEVEL_KEYS: readonly string[] = [...new Set(PACKAGE_SHEET.filter((s) => s.internal).flatMap((s) => s.fields.map((f) => f.path.split('.')[0])))];

function variantLine(v: PackageVariant): string {
  const bits: string[] = [];
  if (v.videos) bits.push(`${v.videos} videos`);
  if (v.minCreators) bits.push(`${v.minCreators}+ creadores`);
  if (v.pricing) bits.push(`precio ${v.currency ? `${v.currency} ` : ''}${v.pricing}`);
  if (v.productionFee) bits.push(`production fee ${v.productionFee}`);
  if (v.mediaCommitment) bits.push(`media commitment ${v.mediaCommitment}`);
  const deliv = v.deliverables?.length ? ` Entregables: ${v.deliverables.join('; ')}.` : '';
  return `  - ${v.name || 'Única'}: ${bits.join(', ') || 'sin datos'}.${deliv}`;
}

/**
 * Lo que el asistente del hub sabe de un pack (markdown, en español). Parte de la
 * vista externa: los economics y las notas internas no entran NUNCA, aunque alguien
 * los pregunte.
 */
export function assistantPackageContext(pkg: Package): string {
  const p = externalPackageView(pkg);
  const lines: string[] = [`- **${p.name}**${p.description ? `: ${p.description.slice(0, 200)}` : ''}`];
  if (p.availableMarkets.length) lines.push(`  Mercados: ${p.availableMarkets.join(', ')}.`);
  const vs = effectiveVariants(p);
  if (vs.length) {
    lines.push(`  Variantes (${vs.length}):`);
    for (const v of vs) lines.push(variantLine(v));
  }
  const e = p.eligibility;
  if (e && (e.segments?.length || e.accountSizes?.length || e.clientType)) {
    const bits: string[] = [];
    if (e.segments?.length) bits.push(`segmentos ${e.segments.join(', ')}`);
    if (e.accountSizes?.length) bits.push(`tamaño de cuenta ${e.accountSizes.join(', ')}`);
    if (e.clientType) bits.push(PACKAGE_SHEET_ENUM_LABELS[e.clientType] ?? e.clientType);
    lines.push(`  Elegibilidad: ${bits.join('; ')}.`);
  }
  for (const s of PACKAGE_SHEET) {
    if (s.internal || s.custom === 'variants') continue;
    for (const f of s.fields) {
      const raw = getPath(p, f.path);
      if (!filled(raw)) continue;
      const value = f.type === 'select' ? (PACKAGE_SHEET_ENUM_LABELS[String(raw)] ?? String(raw)) : String(raw);
      lines.push(`  ${f.label}: ${value.slice(0, 300)}`);
    }
  }
  if (p.links?.length) lines.push(`  Links: ${p.links.map((l) => `[${l.label}](${l.url})`).join(' · ')}`);
  return lines.join('\n');
}
